import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import {BookmarkCheck, LogOut, Menu, Sparkles, User, X} from 'lucide-react'
import { useState } from 'react'
import AuthModal from './AuthModal'
import SavedEstimatesDrawer from './SavedEstimatesDrawer'

const LINKS = [
  { href: '#tattoo-calculator', label: 'Tattoo Cost' },
  { href: '#laser-calculator', label: 'Laser Removal' },
  { href: '#pain-matrix', label: 'Pain & Price' },
  { href: '#niche-styles', label: 'Niche Styles' },
  { href: '#faq', label: 'FAQ' },
]

function Navbar() {
  const [open, setOpen] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { isAuthenticated, user } = useAuth()

  const handleNavClick = (href: string) => {
    setOpen(false)
    const el = document.querySelector(href)
    el?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleAccountClick = () => {
    setOpen(false)
    if (isAuthenticated) {
      setDrawerOpen(true)
    }
    else {
      setAuthOpen(true)
    }
  }

  return (
    <header className="sticky top-0 z-50 border-b border-amber-500/10 bg-[#0a0908]/95 backdrop-blur-md">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded focus:bg-amber-500 focus:px-4 focus:py-2 focus:text-black"
      >
        Skip to content
      </a>
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-amber-400" aria-hidden="true" />
          <span className="font-serif text-lg font-semibold tracking-wide text-white">
            Tattoo<span className="text-amber-400">Cost</span>Calculator
          </span>
        </a>

        <ul className="hidden items-center gap-8 lg:flex">
          {LINKS.map(link => (
            <li key={link.href}>
              <button
                type="button"
                onClick={() => handleNavClick(link.href)}
                className="text-sm font-medium text-slate-300 transition-colors hover:text-amber-400"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            type="button"
            onClick={handleAccountClick}
            className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:border-amber-400/40 hover:text-amber-400"
          >
            {isAuthenticated
              ? <BookmarkCheck className="h-4 w-4" aria-hidden="true" />
              : <User className="h-4 w-4" aria-hidden="true" />}
            {isAuthenticated ? 'My Estimates' : 'Sign In'}
          </button>
          {isAuthenticated && (
            <button
              type="button"
              aria-label="Sign out"
              onClick={() => lumi.auth.signOut()}
              className="rounded-full p-2.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-amber-400"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={() => handleNavClick('#tattoo-calculator')}
            className="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-black shadow-lg shadow-amber-500/20 transition-transform hover:scale-105 hover:bg-amber-400"
          >
            Get My Estimate
          </button>
        </div>

        <button
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          className="rounded-md p-2 text-slate-200 lg:hidden"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open ? (
        <div className="border-t border-amber-500/10 bg-[#0a0908] px-4 py-4 lg:hidden">
          <ul className="flex flex-col gap-1">
            {LINKS.map(link => (
              <li key={link.href}>
                <button
                  type="button"
                  onClick={() => handleNavClick(link.href)}
                  className="block w-full rounded-lg px-3 py-3 text-left text-base font-medium text-slate-200 hover:bg-white/5 hover:text-amber-400"
                >
                  {link.label}
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={handleAccountClick}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-3 text-left text-base font-medium text-slate-200 hover:bg-white/5 hover:text-amber-400"
              >
                {isAuthenticated
                  ? <BookmarkCheck className="h-4 w-4" aria-hidden="true" />
                  : <User className="h-4 w-4" aria-hidden="true" />}
                {isAuthenticated ? `My Estimates${user?.email ? ` (${user.email})` : ''}` : 'Sign In'}
              </button>
            </li>
            {isAuthenticated && (
              <li>
                <button
                  type="button"
                  onClick={() => lumi.auth.signOut()}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-3 text-left text-base font-medium text-slate-200 hover:bg-white/5 hover:text-amber-400"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign Out
                </button>
              </li>
            )}
          </ul>
        </div>
      ) : null}

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <SavedEstimatesDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  )
}

export default Navbar
